import React, { useCallback, CSSProperties } from 'react'; // Added CSSProperties
import { Edge, useReactFlow, MarkerType, useEdges } from 'reactflow';
import { EdgeData, EdgeShape } from '../../../types'; // Assuming types are in src/types
import useStore from '../../../store/useStore'; // Assuming zustand store handles updates
import { shallow } from 'zustand/shallow';
import useDefaultEdgeStore from '../../../store/useDefaultEdgeStore'; // Import the default edge store
import { Check, RotateCcw, Copy } from 'lucide-react'; // Icons for buttons, Added Check, RotateCcw, Copy
import ColorPicker from '../../shared/ColorPicker';

interface EdgeConfigRendererProps {
  edge: Edge<EdgeData>;
}

// --- Shared Label Styling ---
const getLabelStyles = (strokeColor: string | undefined): CSSProperties => ({
  padding: '2px 4px',
  fontSize: '10px',
  fontWeight: 500,
  textAlign: 'center',
  pointerEvents: 'all',
  color: strokeColor || '#555555', // Use edge stroke color or default
  // Assuming --edge-label-shadow-color is defined globally (e.g., in index.css as white)
  textShadow: '-1px -1px 0 var(--edge-label-shadow-color, white), 1px -1px 0 var(--edge-label-shadow-color, white), -1px 1px 0 var(--edge-label-shadow-color, white), 1px 1px 0 var(--edge-label-shadow-color, white)',
  maxHeight: '76px',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  lineHeight: '1.2',
});

const labelBgStyle: CSSProperties = {
  fill: 'transparent', // Make background transparent
};
// --- End Shared Label Styling ---

const DEFAULT_COLOR = '#b1b1b7'; // Default React Flow color

const EdgeConfigRenderer: React.FC<EdgeConfigRendererProps> = ({ edge }) => {
  const { setEdges } = useReactFlow();
  const allEdges = useEdges<EdgeData>(); // Get all edges for Apply to All
  const { takeSnapshot } = useStore(state => ({
    takeSnapshot: state.takeSnapshot
  }), shallow);

  // --- Default Values ---
  // Note: Default label styles will be applied during reset/make default
  const defaultEdgeData: Partial<EdgeData> = {
    shape: 'default',
    color: '#6b7280', // Gray color (using Tailwind gray-500)
    thickness: 2, // Changed from 1 to 2px
    animated: false,
    markerStart: undefined, // Use undefined for no marker
    markerEnd: undefined,   // Use undefined for no marker
    points: [], // Default to no control points
    // label: '', // Label is handled separately now
  };
  const defaultEdgeStyle = {
    stroke: defaultEdgeData.color,
    strokeWidth: defaultEdgeData.thickness,
  };
  const defaultMarkerStart = undefined;
  const defaultMarkerEnd = undefined;
  const defaultAnimated = false;
  const defaultType = 'editable-edge';
  const defaultLabel = '';



  // --- Event Handlers ---
  const handleShapeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const newShape = event.target.value as EdgeShape;
    const newType = (newShape === 'default' || newShape === 'bezier') ? 'editable-edge' : newShape;

    takeSnapshot(); // Snapshot before update
    setEdges((eds) =>
      eds.map((e) => {
        if (e.id === edge.id) {
          const currentData = e.data || {};
          const currentLabel = currentData.label || e.label || ''; // Get label from data or top-level
          const currentStroke = e.style?.stroke || currentData?.color || defaultEdgeStyle.stroke;
          let updatedEdge: Partial<Edge<EdgeData>> = { ...e };

          // Update type and data.shape
          updatedEdge.type = newType;
          updatedEdge.data = { ...currentData, shape: newShape };

          // Ensure data object exists
          if (!updatedEdge.data) updatedEdge.data = {};
          // Always store label in data, clear top-level label
          updatedEdge.data.label = currentLabel;
          delete updatedEdge.label;

          // Apply consistent label styling (React Flow uses this prop)
          updatedEdge.labelStyle = getLabelStyles(currentStroke);
          updatedEdge.labelBgStyle = labelBgStyle;

          return updatedEdge as Edge<EdgeData>; // Cast back to full Edge type
        }
        return e;
      })
    );
  }, [edge.id, setEdges, takeSnapshot]); // Added dependencies

  // Color Picker Handler
  const handleColorChange = (colorValue: string | undefined) => {
    if (colorValue === undefined) {
      // Default to a standard color if undefined is selected
      colorValue = DEFAULT_COLOR;
    }
    
    takeSnapshot();
    setEdges((eds) =>
      eds.map((e) => {
        if (e.id === edge.id) {
          const newStyle = { ...(e.style || {}), stroke: colorValue };
          return {
            ...e,
            style: newStyle,
            data: { ...(e.data || {}), color: colorValue }, // Keep data.color in sync
            labelStyle: getLabelStyles(colorValue), // Update label style with new color
            labelBgStyle: labelBgStyle, // Ensure bg style is applied
             // Update marker colors if they exist
             markerStart: e.markerStart && typeof e.markerStart === 'object' ? { ...e.markerStart, color: colorValue } : e.markerStart,
             markerEnd: e.markerEnd && typeof e.markerEnd === 'object' ? { ...e.markerEnd, color: colorValue } : e.markerEnd,
          };
        }
        return e;
      })
    );
  };


  const handleThicknessChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (isNaN(value)) return;

    takeSnapshot();
    setEdges((eds) =>
      eds.map((e) => {
        if (e.id === edge.id) {
          return {
            ...e,
            style: { ...(e.style || {}), strokeWidth: value },
            data: { ...(e.data || {}), thickness: value }, // Keep data.thickness in sync
             // Re-apply markers to ensure fixed size
             markerStart: e.markerStart,
             markerEnd: e.markerEnd,
          };
        }
        return e;
      })
    );
  };

  const handleAnimationToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    takeSnapshot();
    setEdges((eds) =>
      eds.map((e) => {
        if (e.id === edge.id) {
          return {
            ...e,
            animated: isChecked,
            data: { ...(e.data || {}), animated: isChecked }, // Keep data.animated in sync
          };
        }
        return e;
      })
    );
  };

  // Directly update marker props using setEdges for robustness
  const handleMarkerStartToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    takeSnapshot();
    setEdges((eds) =>
      eds.map((e) => {
        if (e.id === edge.id) {
          const currentStroke = e.style?.stroke || e.data?.color || defaultEdgeStyle.stroke;
          const newMarkerStart = isChecked
            ? {
                type: MarkerType.ArrowClosed,
                color: currentStroke,
                width: 12, height: 12, strokeWidth: 1,
                orient: 'auto-start-reverse'
              }
            : undefined;
          return { ...e, markerStart: newMarkerStart };
        }
        return e;
      })
    );
  }, [edge.id, setEdges, takeSnapshot]); // Added dependencies

  // Directly update marker props using setEdges for robustness
  const handleMarkerEndToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    takeSnapshot();
    setEdges((eds) =>
      eds.map((e) => {
        if (e.id === edge.id) {
           const currentStroke = e.style?.stroke || e.data?.color || defaultEdgeStyle.stroke;
          const newMarkerEnd = isChecked
            ? {
                type: MarkerType.ArrowClosed,
                color: currentStroke,
                width: 12, height: 12, strokeWidth: 1
              }
            : undefined;
          return { ...e, markerEnd: newMarkerEnd };
        }
        return e;
      })
    );
  }, [edge.id, setEdges, takeSnapshot]); // Added dependencies


  // --- Reset, Apply All, and Make Default Handlers ---
  // Get the setDefaultEdgeProperties function from the store
  const setDefaultEdgeProperties = useDefaultEdgeStore(state => state.setDefaultEdgeProperties);

  const handleMakeDefault = useCallback(() => {
    takeSnapshot();
    const currentEdge = allEdges.find(e => e.id === edge.id);
    if (!currentEdge) return;

    const { data, style, animated, markerStart, markerEnd, type } = currentEdge;
    const currentStroke = style?.stroke || data?.color || defaultEdgeStyle.stroke;

    // Create the edge data object to store as default
    const edgeDataToStore: Partial<EdgeData> = {
      shape: data?.shape || 'default',
      color: currentStroke,
      thickness: typeof data?.thickness === 'number' ? data.thickness :
                (typeof style?.strokeWidth === 'number' ? style.strokeWidth : 2),
      animated: data?.animated || animated || false,
      points: [], // Default to no control points for new edges
      // Label is stored top-level or in data based on type
    };

    // Create the edge style object to store as default, ensuring stroke is always a string
    const edgeStyleToStore = {
      stroke: currentStroke || DEFAULT_COLOR, // Fallback to default color if undefined
      strokeWidth: typeof style?.strokeWidth === 'number' ? style.strokeWidth :
                  (typeof data?.thickness === 'number' ? data.thickness : 2),
    };

    // Update the global default edge store with 6 core arguments
    setDefaultEdgeProperties(
      edgeDataToStore,
      edgeStyleToStore,
      markerStart as any, // Using 'any' to bypass potential complex MarkerType issues
      markerEnd as any,   // Using 'any' to bypass potential complex MarkerType issues
      animated || false,
      type || 'editable-edge'
      // Omitting label, labelStyle, labelBgStyle as they might be derived later
    );

  }, [edge.id, allEdges, takeSnapshot, setDefaultEdgeProperties]);

  const handleReset = useCallback(() => {
    takeSnapshot();
    setEdges((eds: Edge<EdgeData>[]) =>
      eds.map((e: Edge<EdgeData>) => {
        if (e.id === edge.id) {
          // Reset label based on default type
          let resetLabelProp: string | undefined = undefined;
          let resetDataLabel: string | undefined = undefined;
          if (defaultType === 'editable-edge') {
            resetDataLabel = defaultLabel;
          } else {
            resetLabelProp = defaultLabel;
          }

          return {
            ...e,
            type: defaultType,
            data: { ...(e.data || {}), ...defaultEdgeData, label: resetDataLabel }, // Apply defaults, set data label
            style: { ...e.style, ...defaultEdgeStyle },
            animated: defaultAnimated,
            markerStart: defaultMarkerStart,
            markerEnd: defaultMarkerEnd,
            label: resetLabelProp, // Set top-level label
            // Apply generated label styles directly based on default stroke
            labelStyle: getLabelStyles(defaultEdgeStyle.stroke),
            labelBgStyle: labelBgStyle,
          };
        }
        return e;
      })
    );
  }, [edge.id, setEdges, takeSnapshot]);

  const handleApplyToAll = useCallback(() => {
    takeSnapshot();
    const currentEdge = allEdges.find(e => e.id === edge.id);
    if (!currentEdge) return;

    const { data, style, animated, markerStart, markerEnd, type, label } = currentEdge;
    const dataToApply = data || {};
    const styleToApply = style || {};
    const labelToApply = String(label || dataToApply.label || '');

    setEdges((eds: Edge<EdgeData>[]) =>
      eds.map((e: Edge<EdgeData>) => {
        // Apply to all edges
        let updatedEdge: Partial<Edge<EdgeData>> = { ...e };
        const currentData = e.data || {};

        updatedEdge.type = type; // Apply type
        updatedEdge.style = { ...(e.style || {}), ...styleToApply };
        updatedEdge.animated = animated;
        updatedEdge.markerStart = markerStart;
        updatedEdge.markerEnd = markerEnd;
        // Apply generated label style based on the applied stroke color
        updatedEdge.labelStyle = getLabelStyles(updatedEdge.style?.stroke);
        updatedEdge.labelBgStyle = labelBgStyle; // Keep applying the static bg style

        // Apply data properties, preserving unrelated data
        updatedEdge.data = {
          ...currentData,
          shape: dataToApply.shape,
          color: dataToApply.color,
          thickness: dataToApply.thickness,
          animated: dataToApply.animated,
          // Points are not applied globally
        };

        // Apply label to correct location based on the *applied* type
        if (type === 'editable-edge') {
            updatedEdge.data.label = labelToApply;
            delete updatedEdge.label;
        } else {
            updatedEdge.label = labelToApply;
            if (updatedEdge.data) {
                delete updatedEdge.data.label;
            }
        }

        return updatedEdge as Edge<EdgeData>;
      })
    );
  }, [edge.id, allEdges, setEdges, takeSnapshot]);


  // --- Current Values ---
  const data = edge.data || {};
  const style = edge.style || {};
  const currentColor = data.color || style.stroke || DEFAULT_COLOR;

  // Common input styling
  const inputClasses = "w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out";
  const labelClasses = "block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1";

  return (
    <div className="space-y-3">
      {/* Toolbar-like section for buttons */}
      <div className="flex justify-end space-x-1 mb-3 -mt-2 -mr-2">
        <button
          onClick={handleReset}
          title="Reset to Defaults"
          className="flex items-center p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <RotateCcw size={14} className="mr-1" />
          <span className="text-xs">Reset</span>
        </button>
        <button
          onClick={handleApplyToAll}
          title="Apply Style to All Edges"
          className="flex items-center p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <Copy size={14} className="mr-1" />
          <span className="text-xs">Apply to All</span>
        </button>
        <button
          onClick={handleMakeDefault}
          title="Make Current Style Default for New Edges"
          className="flex items-center p-1.5 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <Check size={14} className="mr-1" />
          <span className="text-xs">Make Default</span>
        </button>
      </div>

      {/* Label - Update to handle label potentially being top-level */}
      <div>
        <div className="flex justify-between">
          <label htmlFor="edge-label" className={labelClasses}>Label</label>
          <span className="text-xs text-gray-500">
            {String(edge.label || data.label || '').length}/64 {/* Ensure value is string for length */}
          </span>
        </div>
        <input
          id="edge-label"
          type="text"
          value={String(edge.label || data.label || '')} // Ensure value is string
          onChange={(e) => {
            const newLabel = e.target.value.slice(0, 64);
            takeSnapshot();
            setEdges((eds) =>
              eds.map((currentEdge) => {
                if (currentEdge.id === edge.id) {
                  // Create a new data object to avoid potential mutation issues
                  const updatedData = { ...(currentEdge.data || {}) }; // Renamed variable
                  let updatedEdge: Partial<Edge<EdgeData>> = { ...currentEdge };

                  // Always update label in data, clear top-level label
                  updatedData.label = newLabel;
                  updatedEdge.label = undefined; // Ensure top-level label is undefined
                  updatedEdge.data = updatedData; // Assign the updated data object

                  // Apply label styles consistently when label changes (React Flow uses this prop)
                  updatedEdge.labelStyle = getLabelStyles(updatedEdge.style?.stroke);
                  updatedEdge.labelBgStyle = labelBgStyle;

                  return updatedEdge as Edge<EdgeData>;
                }
                return currentEdge;
              })
            );
          }}
          placeholder="Edge label"
          maxLength={64}
          className={inputClasses}
        />
      </div>

      {/* Shape and Arrows Section */}
      <div className="space-y-2">
        <div className="flex space-x-2">
          {/* Shape Button Group */}
          <div className="flex-1">
            <div className="flex w-full h-[34px] rounded-md shadow-sm">
              {/* Bezier Button */}
              <button
                type="button"
                onClick={() => handleShapeChange({ target: { value: 'default' } } as React.ChangeEvent<HTMLSelectElement>)}
                className={`flex-1 flex items-center justify-center text-xs font-medium rounded-l border border-gray-200 dark:border-gray-700 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
                  ${(data.shape === 'default' || !data.shape)
                    ? 'bg-blue-500/20 dark:bg-gray-600 text-gray-900 dark:text-gray-50'
                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
              >
                <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 17C11.8302 17 10.0453 1 22 1" stroke="currentcolor" strokeWidth="2"/>
                </svg>
              </button>
              
              {/* Straight Line Button */}
              <button
                type="button"
                onClick={() => handleShapeChange({ target: { value: 'straight' } } as React.ChangeEvent<HTMLSelectElement>)}
                className={`flex-1 flex items-center justify-center text-xs font-medium border-t border-b border-r border-gray-200 dark:border-gray-700 -ml-px focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
                  ${data.shape === 'straight'
                    ? 'bg-blue-500/20 dark:bg-gray-600 text-gray-900 dark:text-gray-50'
                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
              >
                <svg width="24" height="18" viewBox="0 0 24 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 17L23 1" stroke="currentcolor" strokeWidth="2"/>
                </svg>
              </button>
              
              {/* Step Button */}
              <button
                type="button"
                onClick={() => handleShapeChange({ target: { value: 'step' } } as React.ChangeEvent<HTMLSelectElement>)}
                className={`flex-1 flex items-center justify-center text-xs font-medium border-t border-b border-r border-gray-200 dark:border-gray-700 -ml-px focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
                  ${data.shape === 'step'
                    ? 'bg-blue-500/20 dark:bg-gray-600 text-gray-900 dark:text-gray-50'
                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
              >
                <svg width="21" height="18" viewBox="0 0 21 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 17H11V1H21" stroke="currentcolor" strokeWidth="2"/>
                </svg>
              </button>
              
              {/* Smooth Step Button */}
              <button
                type="button"
                onClick={() => handleShapeChange({ target: { value: 'smoothstep' } } as React.ChangeEvent<HTMLSelectElement>)}
                className={`flex-1 flex items-center justify-center text-xs font-medium rounded-r border-t border-b border-r border-gray-200 dark:border-gray-700 -ml-px focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
                  ${data.shape === 'smoothstep'
                    ? 'bg-blue-500/20 dark:bg-gray-600 text-gray-900 dark:text-gray-50'
                    : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
              >
                <svg width="21" height="18" viewBox="0 0 21 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 17H7C9.20914 17 11 15.2091 11 13V5C11 2.79086 12.7909 1 15 1H21" stroke="currentcolor" strokeWidth="2"/>
                </svg>
              </button>
            </div>
          </div>
          
          {/* Arrows & Animation Button Group */}
          <div className="flex h-[34px] rounded-md shadow-sm">
            {/* Start Arrow Button */}
            <button
              type="button"
              onClick={() => {
                const isActive = !!edge.markerStart;
                takeSnapshot();
                handleMarkerStartToggle({ target: { checked: !isActive } } as React.ChangeEvent<HTMLInputElement>);
              }}
              className={`flex items-center justify-center px-3 text-xs font-medium rounded-l border border-gray-200 dark:border-gray-700 focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
                ${edge.markerStart
                  ? 'bg-blue-500/20 dark:bg-gray-600 text-gray-900 dark:text-gray-50'
                  : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              title="Start Arrow"
            >
              <svg width="19" height="13" viewBox="0 0 19 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.5 6.5H18.5" stroke="currentcolor" strokeWidth="2"/>
                <path d="M-2.84124e-07 6.5L8.25 0.870836L8.25 12.1292L-2.84124e-07 6.5Z" fill="currentcolor"/>
              </svg>
            </button>
            
            {/* End Arrow Button */}
            <button
              type="button"
              onClick={() => {
                const isActive = !!edge.markerEnd;
                takeSnapshot();
                handleMarkerEndToggle({ target: { checked: !isActive } } as React.ChangeEvent<HTMLInputElement>);
              }}
              className={`flex items-center justify-center px-3 text-xs font-medium border-t border-b border-r border-gray-200 dark:border-gray-700 -ml-px focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
                ${edge.markerEnd
                  ? 'bg-blue-500/20 dark:bg-gray-600 text-gray-900 dark:text-gray-50'
                  : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              title="End Arrow"
            >
              <svg width="19" height="13" viewBox="0 0 19 13" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'rotate(180deg)' }}>
                <path d="M6.5 6.5H18.5" stroke="currentcolor" strokeWidth="2"/>
                <path d="M-2.84124e-07 6.5L8.25 0.870836L8.25 12.1292L-2.84124e-07 6.5Z" fill="currentcolor"/>
              </svg>
            </button>
            
            {/* Animate Button */}
            <button
              type="button"
              onClick={() => {
                const isActive = data.animated ?? edge.animated ?? false;
                takeSnapshot();
                handleAnimationToggle({ target: { checked: !isActive } } as React.ChangeEvent<HTMLInputElement>);
              }}
              className={`flex items-center justify-center px-3 text-xs font-medium rounded-r border-t border-b border-r border-gray-200 dark:border-gray-700 -ml-px focus:z-10 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500
                ${(data.animated ?? edge.animated ?? false)
                  ? 'bg-blue-500/20 dark:bg-gray-600 text-gray-900 dark:text-gray-50'
                  : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              title="Animate Edge"
            >
              Animate
            </button>
          </div>
        </div>
      </div>

      {/* Color Picker */}
      <div>
        <label className={labelClasses}>Color</label>
        <ColorPicker
          value={currentColor}
          onChange={handleColorChange}
          showOpacitySlider={false}
          portalRoot={document.body}
        />
      </div>


      {/* Thickness */}
      <div>
        <label htmlFor="edge-thickness" className={labelClasses}> {/* Apply standard label classes */}
          Thickness ({data.thickness || style.strokeWidth || 1}px)
        </label>
        <input
          id="edge-thickness"
          type="range"
          min="1"
          max="10"
          step="1"
          value={String(data.thickness || style.strokeWidth || 1)}
          onChange={handleThicknessChange}
          className="mt-1 block w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 dark:focus:ring-offset-gray-800" // Added focus styles
        />
      </div>

      {/* No content - removing old Arrows section */}
    </div>
  );
};



export default EdgeConfigRenderer;
